import React from "react";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PreloaderTable from "../../../lib/components/PreloaderTable";
import EmptyState from "../../../lib/components/EmptyState";
import { moneyFormat } from "../../../utils/moneyFormat";
import MarketplaceStatusBadge from "./MarketplaceStatusBadge";
import {
  formatCategoryLabel,
  getMonthlyAmount,
  isItemLocked,
  resolveEligiblePlans,
} from "../utils";

/**
 * Catalogue table with S/N and actions. View navigates to detail page.
 * Shows a skeleton whenever `isLoading` is true (initial load, filter, page change).
 */
const COLUMN_COUNT = 9;

const AddonsDataTable = ({
  items = [],
  plans = [],
  rulesByCode = {},
  isLoading = false,
  page = 1,
  limit = 20,
  onView,
  onEdit,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <PreloaderTable
        isLoading
        columns={COLUMN_COUNT}
        rows={Math.min(Math.max(Number(limit) || 8, 5), 10)}
        className="marketplace-table-skeleton"
      />
    );
  }

  if (!items.length) {
    return (
      <div className="p-4">
        <EmptyState isEmpty message="No marketplace add-ons match your filters." />
      </div>
    );
  }

  const startIndex = (Math.max(1, page) - 1) * Math.max(1, limit);

  return (
    <div className="table-responsive">
      <Table className="marketplace-table" size="small">
        <TableHead>
          <TableRow>
            <TableCell width={64}>S/N</TableCell>
            <TableCell>ADD-ON ITEM</TableCell>
            <TableCell>KIND</TableCell>
            <TableCell>CATEGORY</TableCell>
            <TableCell>PRICE / BILLING</TableCell>
            <TableCell>ELIGIBLE PLANS</TableCell>
            <TableCell>PURCHASES</TableCell>
            <TableCell>STATUS</TableCell>
            <TableCell align="right">ACTIONS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => {
            const eligible = resolveEligiblePlans(item, plans);
            const category = formatCategoryLabel(item, rulesByCode);
            const amount = getMonthlyAmount(item);
            const popular = Boolean(item.popular || item.isPopular);
            const locked = isItemLocked(item);
            const sn = startIndex + index + 1;

            return (
              <TableRow key={item.id || item.code} hover>
                <TableCell className="text-secondary">{sn}</TableCell>
                <TableCell>
                  <div className="fw-medium d-inline-flex align-items-center flex-wrap">
                    {item.name || item.code}
                    {popular ? (
                      <span className="marketplace-badge is-popular">Popular</span>
                    ) : null}
                  </div>
                  {item.code ? (
                    <div className="text-secondary fs-xs mt-1 mono">{item.code}</div>
                  ) : null}
                  {String(item.kind).toUpperCase() === "LIMIT" && item.unitValue != null ? (
                    <div className="text-secondary fs-xs mt-1">
                      +{Number(item.unitValue).toLocaleString()}
                      {item.unitLabel ? ` ${item.unitLabel}` : ""} / unit
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>
                  <span className="marketplace-badge is-plan">{item.kind || "—"}</span>
                </TableCell>
                <TableCell>
                  <span className="marketplace-badge is-category">{category}</span>
                </TableCell>
                <TableCell>
                  {amount > 0 ? (
                    <>
                      <span className="fw-medium">{moneyFormat(amount)}</span>
                      <span className="text-secondary"> /monthly</span>
                    </>
                  ) : (
                    <span className="text-secondary">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="d-flex flex-wrap gap-1">
                    {eligible.length ? (
                      eligible.map((plan) => (
                        <span key={plan.id} className="marketplace-badge is-plan">
                          {plan.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-secondary fs-sm">None</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{Number(item.purchaseCount ?? item.purchases ?? 0)}</TableCell>
                <TableCell>
                  <MarketplaceStatusBadge status={item.status} />
                </TableCell>
                <TableCell align="right">
                  <div className="d-inline-flex align-items-center">
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => onView?.(item)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit?.(item)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        locked
                          ? "Cannot hard-delete — item has purchases. Retire instead."
                          : "Hard delete"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={locked}
                          onClick={() => onDelete?.(item)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default AddonsDataTable;
